/**
 * ============================================================
 *  BINDINGS   (WTM-022)
 * ============================================================
 *  A composed template carries the words it was built with. A
 *  live site has a contractor's own: their name, their services,
 *  their phone number, their photographs.
 *
 *  This is the join. One table, applied to every section, which
 *  is what makes the export generic rather than a special case
 *  per design: a field is either something the host knows about
 *  (bound) or something the author wrote (kept).
 *
 *  THE RULE WHEN THE HOST KNOWS NOTHING. A bound field with no
 *  value falls back to what was authored, never to an empty box.
 *  A page that renders "undefined" or a blank heading because a
 *  contractor skipped a step is worse than one showing the
 *  design's own placeholder.
 * ============================================================
 */

/** Index out of a key like `card3Title` -> 3. Zero when there is none. */
function indexOf(key) {
  const found = /(\d+)/.exec(key);
  return found ? Number(found[1]) : 0;
}

/**
 * What the host has for one field key, or undefined to keep the authored text.
 *
 * Ordered most specific first. `services` and `gallery` are the two lists the
 * host actually collects, so anything that repeats reads from one of them.
 */
export function bind(view, key, section) {
  const n = indexOf(key);
  const category = (section && section.category) || "";
  const service = n > 0 ? view.services?.[n - 1] : undefined;

  /* -- the business itself ------------------------------------- */
  if (key === "brandName") return view.name;

  // Footers that sign the page with a business name sign it with THIS one.
  // By variant rather than by pool, because a footer whose title is a parting
  // line ("Thanks for stopping by") is not a place to stamp a company name.
  const variant = (section && section.variant) || "";
  if (key === "title" && (variant === "footer-explore" || variant === "footer-brand" || variant === "footer-columns")) {
    return view.name;
  }
  if (key === "phone" || key === "buttonSecondary") return view.hasPhone ? view.phone : undefined;
  // The host asks for a tagline. Where a design has a line under the name,
  // that is what it is for.
  if (key === "brandTagline") return view.slogan || undefined;
  if (key === "copyright") return `© ${view.year} ${view.name}. All rights reserved.`;

  /* -- services ------------------------------------------------- */
  if (/^card\d+Title$/.test(key) || /^row\d+Title$/.test(key) || /^block\d+Title$/.test(key)) {
    return service?.title;
  }
  if (/^card\d+Desc$/.test(key) || /^row\d+Desc$/.test(key) || /^block\d+Desc$/.test(key)) {
    return service?.description;
  }

  /* -- pictures ------------------------------------------------- */
  if (key === "image") return view.heroImage || undefined;
  if (/^image\d+$/.test(key)) {
    // Galleries come from the gallery; a card's picture comes from its own
    // service, because a service card showing an unrelated job photograph is
    // worse than one showing none.
    return view.gallery?.[n - 1] || service?.image || undefined;
  }
  if (key === "imageLogo") return undefined;

  /* -- the places the host has words for ------------------------ */
  if (key === "aboutText" || key === "body") return view.aboutText || undefined;

  // An About section is the one place the host actually collects prose, so it
  // gets it. Headlines stay authored everywhere: the builder asks a
  // contractor for their name, services and phone number, not for a slogan,
  // and inventing one for them is how a page ends up sounding like nobody.
  if (category === "About us" && key === "subtitle") return view.aboutShort || view.aboutText || undefined;
  if (category === "About us" && key === "eyebrow") return undefined;

  return undefined;
}

/**
 * The value a field should render with.
 *
 * `authored` is what the template was composed with — the design's own words,
 * which are the fallback rather than the exception.
 */
export function value(view, key, authored, section) {
  const bound = bind(view, key, section);
  const text = typeof bound === "string" ? bound.trim() : "";
  return text || authored || "";
}

/**
 * How many of a repeating thing to draw.
 *
 * A services grid built for four, on a contractor who listed two, should draw
 * two — not two real ones and two of the design's placeholders, which reads as
 * a half-finished page. Zero of them means the design's own count stands, so
 * a preview with nothing entered still looks like a design.
 */
export function count(view, kind, designed) {
  const list = kind === "gallery" ? view.gallery : view.services;
  if (!Array.isArray(list) || list.length === 0) return designed;
  return Math.min(designed, list.length);
}
